/*
PURPOSE: Build an openable Apple Music web URL from a stored Apple value. The stored slots hold a
catalog ID, not a URL — despite `externalUrls` naming — so a reader that drops one into an href
emits a relative link resolved against Coral's own origin. Lives in the shared package because the
backend (emails) and the community frontend both link to Apple and must not drift.

USE CASES:
- Any surface turning a stored Apple value into a link: feed/discography/recommendation rows, the
  public and admin artist pages, and the notification digest email.

DOMAIN BOUNDARIES:
- Pure string work: validate the stored shape, emit a canonical URL. No I/O, no catalog lookup.
- Owns URL CONSTRUCTION only. Which slot to read is the caller's decision — notably, this never
  consults `externalUrls.streaming`, which the backend's artist resolver does fall back to.

ASSUMPTIONS:
- Album, song and artist are distinct Apple paths whose IDs are NOT interchangeable (an album ID
  under /song/ 404s, and the reverse), so each kind gets its own function rather than a runtime guess.
- Output is always rebuilt from an extracted numeric ID, never a stored string passed through, so a
  caller cannot be handed a third-party URL and every emitted link has one canonical shape.
- The storefront segment is omitted: Apple redirects a storefront-less catalog URL to the viewer's
  own storefront, and Coral does not record the storefront an ID came from.
- Anything not ID-shaped yields null — the caller renders no link rather than a broken one.
- The artist slot is written as a bare ID, and read leniently: an artist URL still yields its ID, so
  a value stored under an older rule still resolves.
*/
const APPLE_MUSIC_BASE = 'https://music.apple.com';
/** A stored value that is already a bare Apple catalog ID. */
const BARE_CATALOG_ID = /^\d+$/;
/**
 * Captures the numeric ID out of an Apple artist URL in every shape MusicBrainz files one under:
 * either Apple host, either scheme, with or without the name slug, and with or without the legacy
 * `id` prefix. Anchored to the Apple hosts and the `/artist/` segment because Discogs, Songkick and
 * Beatport also use numeric artist IDs, and a loose match binds the wrong artist confidently.
 */
const APPLE_ARTIST_URL_ID = /^https?:\/\/(?:music|itunes)\.apple\.com\/[^/]+\/artist\/(?:[^/?#]+\/)?(?:id)?(\d+)(?:[/?#]|$)/i;
/** Link to an Apple album/release ID (`externalIds.appleMusic` on a release). */
export function appleMusicAlbumUrl(albumId) {
    return albumId && BARE_CATALOG_ID.test(albumId) ? `${APPLE_MUSIC_BASE}/album/${albumId}` : null;
}
/** Link to an Apple song ID (`externalIds.appleMusic` on a track). */
export function appleMusicSongUrl(trackId) {
    return trackId && BARE_CATALOG_ID.test(trackId) ? `${APPLE_MUSIC_BASE}/song/${trackId}` : null;
}
/**
 * The Apple artist ID inside a stored value — a bare ID, or any artist URL shape MusicBrainz files.
 * Null when the value is not Apple-artist-shaped.
 */
export function appleMusicArtistId(storedValue) {
    var _a, _b;
    if (!storedValue) {
        return null;
    }
    if (BARE_CATALOG_ID.test(storedValue)) {
        return storedValue;
    }
    return (_b = (_a = APPLE_ARTIST_URL_ID.exec(storedValue)) === null || _a === void 0 ? void 0 : _a[1]) !== null && _b !== void 0 ? _b : null;
}
/** Link to an artist's stored Apple value (`externalUrls.appleMusic`). */
export function appleMusicArtistUrl(storedValue) {
    const artistId = appleMusicArtistId(storedValue);
    return artistId ? `${APPLE_MUSIC_BASE}/artist/${artistId}` : null;
}
